import { motion, AnimatePresence } from 'framer-motion';
import { RotateCcw, X } from 'lucide-react';
import { usePomodoro, usePomodoroTime } from '@/hooks/usePomodoroTimer';
import { useProductTour } from '@/hooks/useProductTour';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { ImmersiveFocusMode } from '@/components/ImmersiveFocusMode';
import { DeepFocusMode } from '@/components/focus/DeepFocusMode';
import { SessionCompleteDialog } from '@/components/SessionCompleteDialog';
import { PostSessionAuditDialog } from '@/components/PostSessionAuditDialog';

function PostSessionAuditWrapper() {
  const { elapsedTime } = usePomodoroTime();
  const { timerMode, completedTask, handlePostSessionAudit, sessionDuration } = usePomodoro();
  const elapsedMinutes = timerMode === 'maker'
    ? Math.round(elapsedTime / 60)
    : sessionDuration;
  return (
    <PostSessionAuditDialog
      isOpen={true}
      task={completedTask}
      elapsedMinutes={elapsedMinutes}
      onSubmit={(data, completeTask) => handlePostSessionAudit(data, completeTask)}
    />
  );
}

export function PomodoroTimer() {
  const {
    activeTask,
    timerState,
    sessionDuration,
    breakDuration,
    timerMode,
    pauseTimer,
    resumeTimer,
    stopTimer,
    skipBreak,
    hasSavedSession,
    resumeSavedSession,
    clearSavedSession,
    handleSessionComplete,
    showPostSessionAudit,
    handlePostSessionAudit,
    completedTask,
    finishTask,
  } = usePomodoro();
  const { notifyAction } = useProductTour();

  const handleStop = () => {
    stopTimer();
    notifyAction('stop-task');
  };

  // Show saved session prompt
  if (timerState === 'idle' && hasSavedSession) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 50, scale: 0.9 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: 50, scale: 0.9 }}
        className={cn(
          "fixed bottom-6 left-4 right-4 sm:left-1/2 sm:right-auto sm:-translate-x-1/2 z-[60]",
          "bg-card/95 backdrop-blur-xl border border-border/50 rounded-2xl shadow-2xl",
          "p-4 sm:min-w-[320px] sm:max-w-[400px]"
        )}
      >
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
          <div className="flex items-center gap-3 w-full sm:w-auto">
            <div className="p-2 rounded-full bg-primary/10 shrink-0">
              <RotateCcw className="h-4 w-4 text-primary" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium">Resume previous session?</p>
              <p className="text-xs text-muted-foreground">You have a saved timer</p>
            </div>
          </div>
          <div className="flex gap-2 w-full sm:w-auto justify-end">
            <Button size="sm" variant="outline" onClick={clearSavedSession}>
              <X className="h-4 w-4" />
            </Button>
            <Button size="sm" onClick={resumeSavedSession}>
              Resume
            </Button>
          </div>
        </div>
      </motion.div>
    );
  }

  // Show session complete dialog FIRST (asks if task needs more time)
  if (timerState === 'complete') {
    return (
      <SessionCompleteDialog
        isOpen={true}
        task={completedTask}
        timerMode={timerMode}
        onComplete={handleSessionComplete}
      />
    );
  }

  // Show post-session audit dialog AFTER session complete (for Manager mode)
  if (showPostSessionAudit) {
    return <PostSessionAuditWrapper />;
  }

  if (timerState === 'idle') return null;

  // Type guard to ensure timerState is one of the expected values
  const activeTimerState = timerState as 'running' | 'paused' | 'break';

  return (
    <AnimatePresence mode="wait">
      {timerMode === 'maker' ? (
        <DeepFocusMode
          key="deep-focus"
          activeTask={activeTask}
          timerState={activeTimerState}
          goalMinutes={sessionDuration || 60}
          onPause={pauseTimer}
          onResume={resumeTimer}
          onStop={handleStop}
          onFinishTask={finishTask}
        />
      ) : (
        <ImmersiveFocusMode
          key="immersive"
          activeTask={activeTask}
          timerState={activeTimerState}
          sessionDuration={sessionDuration}
          breakDuration={breakDuration}
          timerMode={timerMode}
          onPause={pauseTimer}
          onResume={resumeTimer}
          onStop={handleStop}
          onSkipBreak={skipBreak}
          onFinishTask={finishTask}
        />
      )}
    </AnimatePresence>
  );
}
